import React, { useState, useEffect } from 'react';
import { Download, Filter, RefreshCw, AlertCircle, FileText } from 'lucide-react';
import { API_BASE_URL } from '../services/api';

const ReportsPage = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedDays, setSelectedDays] = useState(15);
  const [selectedReport, setSelectedReport] = useState('same-stage');
  const [selectedScheme, setSelectedScheme] = useState('all');
  const [userRole, setUserRole] = useState('');

  const daysOptions = [
    { value: 15, label: '15 Days' },
    { value: 30, label: '30 Days' },
    { value: 60, label: '60 Days' },
    { value: 90, label: '90 Days' }
  ];

  const reportOptions = [
    { value: 'same-stage', label: 'Same Stage Report', endpoint: '/project/report/same-stage', schemeFilter: true },
    { value: 'higher-area', label: 'Variation in Size Report', endpoint: '/project/report/higher-area', schemeFilter: true },
    { value: 'block-status', label: 'Block-wise Status Report', endpoint: '/user/home', adminOnly: true },
  ];

  // Whether the current report supports the scheme filter
  const schemeFilterEnabled = !!reportOptions.find(r => r.value === selectedReport)?.schemeFilter;

  // Distinct schemes present in the fetched data (only for array-based reports)
  const getAvailableSchemes = () => {
    if (!Array.isArray(reportData)) return [];
    return [...new Set(reportData.map(item => item.scheme).filter(Boolean))].sort();
  };

  // Data after applying the scheme filter
  const getFilteredReportData = () => {
    if (!Array.isArray(reportData)) return reportData;
    if (!schemeFilterEnabled || selectedScheme === 'all') return reportData;
    return reportData.filter(item => item.scheme === selectedScheme);
  };

  // Get user role from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setUserRole(user.role || '');
      } catch (err) {
        console.error('Error parsing user data:', err);
      }
    }
  }, []);

  // Filter report options based on user role
  const getAvailableReports = () => {
    if (userRole === 'admin') {
      return reportOptions;
    }
    return reportOptions.filter(option => !option.adminOnly);
  };

  // Fetch report data
  const fetchReportData = async () => {
    setLoading(true);
    setError('');
    setReportData([]);
    
    try {
      const token = localStorage.getItem('token');
      const currentReport = reportOptions.find(r => r.value === selectedReport);
      let url = `${API_BASE_URL}${currentReport.endpoint}`;
      
      // Add days parameter for reports that need it
      if (selectedReport === 'same-stage') {
        url += `?days=${selectedDays}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (response.ok) {
        if (selectedReport === 'block-status') {
          // Transform the status data for block-wise report
          setReportData(data.data || {});
        } else {
          setReportData(data.data || []);
        }
      } else {
        setError(data.message || 'Failed to fetch report data');
      }
    } catch (err) {
      console.error('Error fetching report:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Clear data and fetch new data when report type changes
  useEffect(() => {
    setReportData(selectedReport === 'block-status' ? {} : []);
    setSelectedScheme('all');
    fetchReportData();
  }, [selectedReport]);

  // Fetch data when days selection changes (only for same-stage report)
  useEffect(() => {
    if (selectedReport === 'same-stage') {
      fetchReportData();
    }
  }, [selectedDays]);

  // Download report as CSV
  const downloadCSV = () => {
    if (selectedReport === 'block-status') {
      downloadBlockStatusCSV();
      return;
    }

    const dataToDownload = getFilteredReportData();

    if (!dataToDownload || dataToDownload.length === 0) {
      alert('No data to download');
      return;
    }

    let headers = [];
    let csvData = [];

    // Different CSV structure based on report type
    if (selectedReport === 'same-stage') {
      headers = ['Housing ID', 'Beneficiary Name', 'District', 'Block', 'Panchayat', 'Scheme', 'Current Stage'];
      csvData = dataToDownload.map(item => [
        item.h_id || '',
        item.beneficiary || '',
        item.district || '',
        item.block || '',
        item.panchayat || '',
        item.scheme || '',
        item.stage || ''
      ]);
    } else if (selectedReport === 'higher-area') {
      headers = ['Housing ID', 'Beneficiary Name', 'District', 'Block', 'Panchayat', 'Scheme', 'Planned Area (sq ft)', 'Actual Area (sq ft)'];
      csvData = dataToDownload.map(item => [
        item.h_id || '',
        item.beneficiary || '',
        item.district || '',
        item.block || '',
        item.panchayat || '',
        item.scheme || '',
        item.planned_area || '',
        item.actual_area || ''
      ]);
    }
    
    // Combine headers and data
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const reportName = reportOptions.find(r => r.value === selectedReport)?.label.toLowerCase().replace(/\s+/g, '_') || 'report';
    link.setAttribute('download', `${reportName}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download block status report as CSV
  const downloadBlockStatusCSV = () => {
    if (!reportData || !reportData.status) {
      alert('No data to download');
      return;
    }

    const statusData = reportData.status;
    const blocks = Object.keys(statusData);

    // Define all possible stages
    const stages = [
      'Not Started',
      'Site Marked',
      'Earthwork Completed',
      'FC Laid',
      'Basement Completed',
      'Lintal Level',
      'Lintel laid',
      'Roof Level',
      'Roof Laid',
      'Plastering',
      'Whitewash / Colourwash Progress',
      'Physically Completed',
      'Completed'
    ];

    // Create headers
    const headers = ['Block Name', ...stages];

    // Create data rows
    const csvData = blocks.map(block => {
      const row = [block];
      stages.forEach(stage => {
        row.push(statusData[block][stage] || 0);
      });
      return row;
    });

    // Add total row
    const totalRow = ['Total'];
    stages.forEach(stage => {
      const total = blocks.reduce((sum, block) => sum + (statusData[block][stage] || 0), 0);
      totalRow.push(total);
    });
    csvData.push(totalRow);

    // Combine headers and data
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `block_status_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getCurrentReportLabel = () => {
    return reportOptions.find(r => r.value === selectedReport)?.label || 'Report';
  };

  const renderBlockStatusTable = () => {
    if (!reportData || !reportData.status) {
      return null;
    }

    const statusData = reportData.status;
    const blocks = Object.keys(statusData);

    // Define stage columns with shorter display names
    const stageColumns = [
      { key: 'Not Started', label: 'Not Started', color: 'text-gray-600' },
      { key: 'Site Marked', label: 'Site Marked', color: 'text-gray-600' },
      { key: 'Earthwork Completed', label: 'Earthwork', color: 'text-yellow-600' },
      { key: 'FC Laid', label: 'FC Laid', color: 'text-yellow-600' },
      { key: 'Basement Completed', label: 'Basement', color: 'text-orange-600' },
      { key: 'Lintal Level', label: 'Lintel Level', color: 'text-orange-600' },
      { key: 'Lintel laid', label: 'Lintel Laid', color: 'text-orange-600' },
      { key: 'Roof Level', label: 'Roof Level', color: 'text-blue-600' },
      { key: 'Roof Laid', label: 'Roof Laid', color: 'text-blue-600' },
      { key: 'Plastering', label: 'Plastering', color: 'text-indigo-600' },
      { key: 'Whitewash / Colourwash Progress', label: 'Whitewash', color: 'text-purple-600' },
      { key: 'Physically Completed', label: 'Completed', color: 'text-green-600' },
    ];

    // Calculate totals
    const totals = {};
    stageColumns.forEach(stage => {
      totals[stage.key] = blocks.reduce((sum, block) => 
        sum + (statusData[block][stage.key] || 0), 0
      );
    });

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-blue-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-300 sticky left-0 bg-blue-50 z-10">
                Block Name
              </th>
              {stageColumns.map((stage, idx) => (
                <th 
                  key={idx} 
                  className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200"
                >
                  {stage.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {blocks.map((block, blockIdx) => (
              <tr key={blockIdx} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-300 sticky left-0 bg-white z-10">
                  {block}
                </td>
                {stageColumns.map((stage, stageIdx) => {
                  const value = statusData[block][stage.key] || 0;
                  return (
                    <td 
                      key={stageIdx} 
                      className={`px-3 py-3 text-center text-sm ${value > 0 ? stage.color + ' font-semibold' : 'text-gray-400'} border-r border-gray-200`}
                    >
                      {value || 0}
                    </td>
                  );
                })}
              </tr>
            ))}
            {/* Total Row */}
            <tr className="bg-blue-100 font-bold">
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 border-r border-gray-300 sticky left-0 bg-blue-100 z-10">
                Total
              </td>
              {stageColumns.map((stage, stageIdx) => (
                <td 
                  key={stageIdx} 
                  className="px-3 py-3 text-center text-sm text-gray-900 border-r border-gray-200"
                >
                  {totals[stage.key] || 0}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const renderReportTable = () => {
    if (selectedReport === 'block-status') {
      return renderBlockStatusTable();
    }

    const tableData = getFilteredReportData();

    if (selectedReport === 'same-stage') {
      return (
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Housing Details
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Current Stage
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableData.map((item, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{item.h_id}</div>
                    <div className="text-sm text-gray-600">{item.beneficiary}</div>
                    <div className="text-xs text-gray-500">{item.mobile}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{item.district}</div>
                  <div className="text-sm text-gray-600">{item.block}</div>
                  <div className="text-xs text-gray-500">{item.panchayat}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                    {item.stage}
                  </span>
                  <div className="text-xs text-gray-500 mt-1">{item.scheme}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    } else if (selectedReport === 'higher-area') {
      return (
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Housing Details
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Area Details
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableData.map((item, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{item.h_id}</div>
                    <div className="text-sm text-gray-600">{item.beneficiary}</div>
                    <div className="text-xs text-gray-500">{item.mobile}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{item.district}</div>
                  <div className="text-sm text-gray-600">{item.block}</div>
                  <div className="text-xs text-gray-500">{item.panchayat}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    Allotted: <span className="font-medium">{item.scheme_house_size || 'N/A'} sq ft</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Actual: <span className="font-medium">{item.ins_house_size|| 'N/A'} sq ft</span>
                  </div>
                  <div className="text-xs text-gray-500">{item.scheme}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    
    return null;
  };

  const getRecordCount = () => {
    if (selectedReport === 'block-status') {
      if (reportData && reportData.status) {
        const blocks = Object.keys(reportData.status);
        return `${blocks.length} blocks`;
      }
      return '';
    }
    const count = getFilteredReportData();
    return count && count.length > 0 ? `${count.length} records` : '';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="space-y-6">
          
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-blue-600" />
                  Reports
                </h1>
                <p className="text-gray-600">Generate and download various inspection reports</p>
              </div>
              
              <div className="flex items-center gap-3">
                {/* Scheme Filter (only for reports that support it) */}
                {schemeFilterEnabled && (
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-gray-500" />
                    <select
                      value={selectedScheme}
                      onChange={(e) => setSelectedScheme(e.target.value)}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All Schemes</option>
                      {getAvailableSchemes().map(scheme => (
                        <option key={scheme} value={scheme}>
                          {scheme}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Report Type Filter */}
                <div className="flex items-center gap-2">
                  <select
                    value={selectedReport}
                    onChange={(e) => setSelectedReport(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {getAvailableReports().map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Days Filter (only for relevant reports) */}
                {selectedReport === 'same-stage' && ( 
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-gray-500" />
                    <select
                      value={selectedDays}
                      onChange={(e) => setSelectedDays(parseInt(e.target.value))}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {daysOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Refresh Button */}
                <button
                  onClick={fetchReportData}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>

                {/* Download Button */}
                <button
                  onClick={downloadCSV}
                  disabled={loading || (!reportData || (Array.isArray(reportData) ? reportData.length === 0 : !reportData.status))}
                  className="inline-flex items-center px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download CSV
                </button>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          {/* Data Table */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {getCurrentReportLabel()}
                {selectedReport === 'same-stage' && ` (${selectedDays}+ Days)`}
                {getRecordCount() && (
                  <span className="ml-2 text-sm font-normal text-gray-600">
                    ({getRecordCount()})
                  </span>
                )}
              </h2>
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-blue-600 mb-4" />
                <p className="text-gray-600">Loading report data...</p>
              </div>
            ) : (selectedReport === 'block-status' && reportData && reportData.status) ||
               (selectedReport !== 'block-status' && getFilteredReportData() && getFilteredReportData().length > 0) ? (
              <div className="overflow-x-auto">
                {renderReportTable()}
              </div>
            ) : !loading ? (
              <div className="p-8 text-center">
                <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 mb-2">No data found</p>
                <p className="text-sm text-gray-500">
                  No records found for the selected criteria
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;